/**
 * Aktionen, Checklisten, Saisonplanung (Plan 04): Typen, Schemas und Regeln (Abschnitt 6).
 * Die Regeln sind 1:1 aus dem freigegebenen Prototyp übernommen (app.js: taskState, progress, status, lotsenBedarf).
 */
import { z } from 'zod';
import type { RatingGrade, Reminder, RoleKey } from './types.js';
import { addDays, fmtDate } from './format.js';
import { buildCode } from './codes.js';

/* ---------- Status ---------- */
export const ACTION_STATUSES = ['offen', 'zugesagt', 'bestaetigt', 'abstimmung', 'abgeschlossen', 'ausgefallen'] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];
export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  offen: 'offen',
  zugesagt: 'zugesagt',
  bestaetigt: 'bestätigt',
  abstimmung: 'in Abstimmung',
  abgeschlossen: 'abgeschlossen',
  ausgefallen: 'ausgefallen',
};
export const SEASON_CODES = ['F', 'S', 'H'] as const;
export type SeasonCode = (typeof SEASON_CODES)[number];
export const SEASON_LABELS: Record<SeasonCode, string> = { F: 'Frühjahr', S: 'Sommer', H: 'Herbst' };
/** "H2026" → "Herbst 2026" */
export const seasonLabel = (code: string | null | undefined, year: number | null | undefined) => (year ? `${code ? SEASON_LABELS[code as SeasonCode] ?? code : ''} ${year}`.trim() : 'ohne Saison');
export const seasonKey = (code: string | null | undefined, year: number | null | undefined) => (year ? `${code ?? 'X'}${year}` : '');
export function parseSeasonKey(key: string): { code: SeasonCode | null; year: number } | null {
  const m = /^([FSHX])(\d{4})$/.exec(key);
  if (!m) return null;
  return { code: m[1] === 'X' ? null : (m[1] as SeasonCode), year: Number(m[2]) };
}
/** Saison aus einem Datum: Januar bis Mai Frühjahr, Juni bis September Sommer, Oktober bis Dezember Herbst */
export function seasonOfDate(iso: string): SeasonCode {
  const m = Number(iso.slice(5, 7));
  return m <= 5 ? 'F' : m <= 9 ? 'S' : 'H';
}
/** Sortierwert einer Saison */
export const seasonOrder = (code: string | null | undefined, year: number | null | undefined) => (year ?? 0) * 10 + (code === 'F' ? 1 : code === 'S' ? 2 : code === 'H' ? 3 : 0);

/* ---------- Beteiligte und Kosten ---------- */
export const ACTION_CONTACT_ROLES = ['partner', 'sponsor', 'funder', 'forester', 'owner', 'guide', 'educator', 'hotel', 'wc', 'caterer', 'bus', 'nursery', 'fence', 'press', 'vip', 'supplier_other'] as const;
export type ActionContactRole = (typeof ACTION_CONTACT_ROLES)[number];
export const ACTION_CONTACT_ROLE_LABELS: Record<ActionContactRole, string> = {
  partner: 'Spendenpartner',
  sponsor: 'Förderpartner',
  funder: 'Fördermittelgeber',
  forester: 'Förster',
  owner: 'Waldbesitzer',
  guide: 'Pflanzlotse',
  educator: 'Umweltpädagogik (EWW)',
  hotel: 'Hotel',
  wc: 'Toiletten',
  caterer: 'Caterer',
  bus: 'Bus',
  nursery: 'Baumschule',
  fence: 'Zaunbau',
  press: 'Presse',
  vip: 'VIP',
  supplier_other: 'Lieferant',
};
export const ACTION_CONTACT_STATUSES = ['geplant', 'angefragt', 'bestaetigt', 'gebucht', 'abgesagt'] as const;
export type ActionContactStatus = (typeof ACTION_CONTACT_STATUSES)[number];
export const ACTION_CONTACT_STATUS_LABELS: Record<ActionContactStatus, string> = { geplant: 'geplant', angefragt: 'angefragt', bestaetigt: 'bestätigt', gebucht: 'gebucht', abgesagt: 'abgesagt' };

/* ---------- Baumpreis (Review 26.10.2026) ---------- */
/** Der Baumpreis wird mal brutto, mal netto vereinbart; der Steuersatz schwankt (0, 7, 10,7, 19 %) */
export const PRICE_BASES = ['brutto', 'netto'] as const;
export type PriceBasis = (typeof PRICE_BASES)[number];
/** Netto und Brutto aus Preis, Basis und Steuersatz; ohne Steuersatz bleibt die Gegenseite offen */
export function treePrices(price: number | null | undefined, basis: PriceBasis | null | undefined, vat: number | null | undefined): { net: number | null; gross: number | null } {
  if (price == null) return { net: null, gross: null };
  const r2 = (x: number) => Math.round(x * 100) / 100;
  const f = vat == null ? null : 1 + vat / 100;
  if (basis === 'netto') return { net: price, gross: f == null ? null : r2(price * f) };
  return { gross: price, net: f == null ? null : r2(price / f) };
}

/* ---------- Checklisten-Vorlage ---------- */
export const CHECKLIST_FIELD_TYPES = ['select', 'date', 'dates', 'text', 'number', 'textarea', 'contact', 'file', 'checks', 'reminder'] as const;
export type ChecklistFieldType = (typeof CHECKLIST_FIELD_TYPES)[number];

export const ChecklistFieldDefSchema = z.object({
  key: z.string().min(1).max(40).regex(/^[a-z][a-z0-9_]*$/, 'Schlüssel: Kleinbuchstaben, Ziffern, Unterstrich'),
  label: z.string().min(1).max(160),
  type: z.enum(CHECKLIST_FIELD_TYPES),
  options: z.array(z.string().max(80)).optional(),
  /** Kontaktkategorie für den Picker */
  contact_category: z.string().max(40).optional(),
  /** Rolle in action_contacts, wenn dieses Kontaktfeld gefüllt wird (6.9) */
  role: z.enum(ACTION_CONTACT_ROLES).optional(),
  multi: z.boolean().optional(),
  /** Datei am Datumsfeld / an den Checkboxen */
  file: z.boolean().optional(),
  /** Checkboxen ohne automatisches Datum */
  nodate: z.boolean().optional(),
});
export type ChecklistFieldDef = z.infer<typeof ChecklistFieldDefSchema>;

export const DoneRuleSchema = z.union([
  z.object({ f: z.string(), is: z.array(z.string()) }),
  z.object({ all: z.array(z.string()) }),
  z.object({ any: z.array(z.string()) }),
  z.object({ anyOr: z.array(z.string()) }),
  z.object({ checks: z.string(), min: z.array(z.string()) }),
  z.object({ never: z.literal(true) }),
]);
export type DoneRule = z.infer<typeof DoneRuleSchema>;
export const DONE_RULE_KINDS = [
  { kind: 'f', label: 'Feld hat einen bestimmten Wert' },
  { kind: 'all', label: 'Alle genannten Felder sind gefüllt' },
  { kind: 'any', label: 'Eines der Felder ist gefüllt' },
  { kind: 'checks', label: 'Mindestens diese Kästchen sind angehakt' },
  { kind: 'never', label: 'Nie automatisch (nur von Hand)' },
] as const;
export const doneRuleKind = (r: DoneRule): (typeof DONE_RULE_KINDS)[number]['kind'] => ('f' in r ? 'f' : 'all' in r ? 'all' : 'any' in r || 'anyOr' in r ? 'any' : 'checks' in r ? 'checks' : 'never');

export interface ChecklistGroupDef {
  key: string;
  name: string;
  responsible_role: RoleKey | null;
  sort: number;
  after_action: boolean;
  description?: string | null;
}
export interface ChecklistTemplateItemDef {
  key: string;
  group_key: string;
  title: string;
  sort: number;
  due_offset_days: number | null;
  responsible_role?: RoleKey | null;
  optional: boolean;
  needed_field: string | null;
  fields: ChecklistFieldDef[];
  done_rule: DoneRule;
  active: boolean;
}
export const TemplateItemPatchSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  group_key: z.string().max(40).optional(),
  sort: z.number().int().optional(),
  due_offset_days: z.number().int().min(-730).max(3650).nullable().optional(),
  responsible_role: z.enum(['admin', 'vorstand', 'forst', 'flaeche', 'projekt']).nullable().optional(),
  optional: z.boolean().optional(),
  needed_field: z.string().max(40).nullable().optional(),
  fields: z.array(ChecklistFieldDefSchema).min(1).optional(),
  done_rule: DoneRuleSchema.optional(),
  active: z.boolean().optional(),
});
export type TemplateItemPatch = z.infer<typeof TemplateItemPatchSchema>;
export const TemplateItemCreateSchema = TemplateItemPatchSchema.extend({
  key: z.string().min(1).max(40).regex(/^[a-z][a-z0-9_]*$/),
  title: z.string().min(1).max(200),
  group_key: z.string().min(1).max(40),
  fields: z.array(ChecklistFieldDefSchema).min(1),
  done_rule: DoneRuleSchema,
});
export interface ChecklistTemplateDto {
  id: number;
  name: string;
  action_type_codes: string[];
  is_default: boolean;
  groups: ChecklistGroupDef[];
  items: (ChecklistTemplateItemDef & { id: number })[];
}

/* ---------- Punkt je Aktion ---------- */
export type ChecklistItemState = 'open' | 'done' | 'not_needed';
export type ChecklistValues = Record<string, unknown>;
export interface ChecklistItemDto {
  id: number;
  action_id: number;
  template_item_key: string;
  group_key: string;
  title: string;
  sort: number;
  optional: boolean;
  fields: ChecklistFieldDef[];
  done_rule: DoneRule;
  needed_field: string | null;
  state: ChecklistItemState;
  due_date: string | null;
  due_is_default: boolean;
  default_due: string | null;
  responsible_role: RoleKey | null;
  responsible_user_id: number | null;
  responsible_user_name: string | null;
  reminder_date: string | null;
  reminder_note: string | null;
  values: ChecklistValues;
  /** Kontakte in Kontaktfeldern aufgelöst: feld → [{id, display_name, rating}] */
  contacts: Record<string, { id: number; display_name: string; rating_grade: RatingGrade | null; sub: string | null }[]>;
  completed_at: string | null;
  updated_at: string;
  /** berechnet */
  effective_due: string | null;
  late: boolean;
  summary: string;
  can_edit: boolean;
}
export const ChecklistItemPatchSchema = z.object({
  values: z.record(z.unknown()).optional(),
  due_date: z.string().max(10).nullable().optional(),
  /** true = auf Standardfrist zurück */
  reset_due: z.boolean().optional(),
  responsible_role: z.enum(['admin', 'vorstand', 'forst', 'flaeche', 'projekt']).nullable().optional(),
  responsible_user_id: z.number().int().nullable().optional(),
  reminder_date: z.string().max(10).nullable().optional(),
  reminder_note: z.string().max(500).nullable().optional(),
  updated_at: z.string().optional(),
});
export type ChecklistItemPatch = z.infer<typeof ChecklistItemPatchSchema>;

/* ---------- Aktion: Schemas ---------- */
const str = (n: number) => z.string().max(n).nullable().optional();
const dec = z.number().nullable().optional();
const intn = z.number().int().nullable().optional();
export const ActionBaseSchema = z.object({
  type_code: z.string().max(20).optional(),
  season_code: z.enum(SEASON_CODES).nullable().optional(),
  year: z.number().int().min(2000).max(2100).nullable().optional(),
  date: str(10),
  date_end: str(10),
  time_start: str(5),
  time_end: str(5),
  state_code: str(5),
  region: str(100),
  city: str(120),
  area_id: intn,
  partner_contact_id: intn,
  forester_contact_id: intn,
  owner_contact_id: intn,
  project_lead_user_id: intn,
  team_initials: str(60),
  trees_planned: intn,
  trees_calculated: intn,
  trees_by_participants: intn,
  hectares: dec,
  species_text: str(500),
  species: z.array(z.object({ species_code: z.string().max(50), count: z.number().int().min(0), note: z.string().max(200).nullable().optional() })).optional(),
  participants_planned: intn,
  children_planned: intn,
  participants_actual: intn,
  children_actual: intn,
  donation_pledged: dec,
  donation_received_prev_year: dec,
  donation_received_this_year: dec,
  donation_received_at: str(10),
  culture_costs_expected: dec,
  price_per_tree: dec,
  price_basis: z.enum(PRICE_BASES).optional(),
  vat_rate: z.number().min(0).max(30).nullable().optional(),
  agreements_note: z.string().nullable().optional(),
  planting_comment: z.string().nullable().optional(),
  preparations_note: z.string().nullable().optional(),
  rental_car: str(120),
  impact_report_sent_at: str(10),
  donation_receipt_sent_at: str(10),
  nest_boxes: str(120),
  press_review: z.boolean().optional(),
  notes: z.string().nullable().optional(),
});
export const ActionCreateSchema = ActionBaseSchema.extend({
  partner_contact_id: z.number().int({ message: 'Spendenpartner ist Pflicht' }),
  type_code: z.string().min(1, 'Aktionstyp ist Pflicht').max(20),
  code: z.string().max(120).optional(),
  /** Begehung aus dem Assistenten: Protokoll zur Fläche anlegen (Datum) */
  site_visit_date: str(10),
  /** Beteiligte aus dem Assistenten (z. B. Förster) direkt in die Checkliste */
  confirmed_at: str(10),
});
export type ActionCreate = z.infer<typeof ActionCreateSchema>;
export const ActionPatchSchema = ActionBaseSchema.partial().extend({
  code: z.string().max(120).optional(),
  updated_at: z.string().optional(),
  /** Kennung nach Terminänderung neu bilden (6.7, Bestätigung durch Nutzer) */
  regenerate_code: z.boolean().optional(),
});
export type ActionPatch = z.infer<typeof ActionPatchSchema>;
export const ActionStatusInputSchema = z.object({ status: z.enum(['ausgefallen', 'abgeschlossen', 'auto']), reason: z.string().max(1000).nullable().optional() });
export const ActionContactInputSchema = z.object({
  contact_id: z.number().int(),
  role: z.enum(ACTION_CONTACT_ROLES),
  status: z.enum(ACTION_CONTACT_STATUSES).default('geplant'),
  requested_at: str(10),
  confirmed_at: str(10),
  count: intn,
  note: z.string().max(1000).nullable().optional(),
});
export type ActionContactInput = z.infer<typeof ActionContactInputSchema>;
export const ActionCostInputSchema = z.object({
  category_code: z.string().max(20),
  amount: z.number().min(0),
  invoice_no: str(60),
  invoice_date: str(10),
  supplier_contact_id: intn,
  document_id: intn,
  note: z.string().max(1000).nullable().optional(),
});
export type ActionCostInput = z.infer<typeof ActionCostInputSchema>;

/* ---------- Aktion: DTOs ---------- */
export interface GroupProgress {
  key: string;
  name: string;
  responsible_role: RoleKey | null;
  done: number;
  total: number;
  late: number;
  open: { id: number; key: string; title: string; due: string | null; late: boolean }[];
}
export interface ActionMissing {
  text: string;
  late: boolean;
  ok?: boolean;
}
export interface ActionListItem {
  id: number;
  code: string;
  label: string;
  type_code: string;
  type_label: string;
  season_code: SeasonCode | null;
  year: number | null;
  season: string;
  date: string | null;
  date_end: string | null;
  status: ActionStatus;
  status_label: string;
  status_manual: boolean;
  state_code: string | null;
  state_label: string | null;
  region: string | null;
  city: string | null;
  partner: { id: number; display_name: string } | null;
  forester: { id: number; display_name: string; rating_grade: RatingGrade | null } | null;
  area: { id: number; name: string } | null;
  project_lead: { id: number; display_name: string; initials: string | null } | null;
  trees_planned: number | null;
  hectares: number | null;
  participants_planned: number | null;
  participants_actual: number | null;
  donation_pledged: number | null;
  progress: GroupProgress[];
  progress_pct: number;
  missing: ActionMissing;
  review_needed: boolean;
  archived_at: string | null;
  updated_at: string;
}
export interface ActionContactDto {
  id: number;
  contact_id: number;
  display_name: string;
  organisation_name: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  rating_grade: RatingGrade | null;
  city: string | null;
  role: ActionContactRole;
  role_label: string;
  status: ActionContactStatus;
  status_label: string;
  requested_at: string | null;
  confirmed_at: string | null;
  count: number | null;
  note: string | null;
  /** Punkt, aus dem die Beteiligung stammt */
  item_id: number | null;
  item_title: string | null;
}
export interface ActionCostDto {
  id: number;
  category_code: string;
  category_label: string;
  amount: number;
  invoice_no: string | null;
  invoice_date: string | null;
  supplier: { id: number; display_name: string } | null;
  document_id: number | null;
  note: string | null;
  per_tree: boolean;
}
export interface ActionDetail extends ActionListItem {
  time_start: string | null;
  time_end: string | null;
  owner: { id: number; display_name: string } | null;
  team_initials: string | null;
  trees_calculated: number | null;
  trees_by_participants: number | null;
  species_text: string | null;
  species: { species_code: string; label: string; count: number; note: string | null }[];
  children_planned: number | null;
  children_actual: number | null;
  donation_received_prev_year: number | null;
  donation_received_this_year: number | null;
  donation_received_at: string | null;
  culture_costs_expected: number | null;
  price_per_tree: number | null;
  price_basis: PriceBasis;
  vat_rate: number | null;
  price_net: number | null;
  price_gross: number | null;
  agreements_note: string | null;
  planting_comment: string | null;
  preparations_note: string | null;
  rental_car: string | null;
  impact_report_sent_at: string | null;
  donation_receipt_sent_at: string | null;
  nest_boxes: string | null;
  press_review: boolean;
  notes: string | null;
  sharepoint_url: string | null;
  status_reason: string | null;
  completed_at: string | null;
  culture_costs_calc: number | null;
  guides: { needed: number; tww: number; confirmed: number; forester: number; short: boolean };
  hints: { kind: 'bad' | 'warn' | 'info'; text: string; item_key?: string }[];
  all_confirmed: boolean;
  checklist: ChecklistItemDto[];
  groups: ChecklistGroupDef[];
  contacts: ActionContactDto[];
  costs: ActionCostDto[];
  cost_total: number;
  /** Forstkosten (Kostenarten „je Baum“) und übrige Aktionskosten wie Fahrt, Verpflegung, Übernachtung */
  cost_forest: number;
  cost_other: number;
  /** nur Forstkosten je Baum */
  cost_per_tree: number | null;
  site_visit: { id: number; status: 'draft' | 'final'; visited_at: string | null } | null;
  area_detail: { id: number; name: string; lat: number | null; lng: number | null; hectares: number | null; status: string } | null;
  forester_confirmed_at: string | null;
  partner_confirmed_at: string | null;
  forester_contract_year: number | null;
  own_due_count: number;
  created_at: string;
}
export interface OpenItemRow {
  item_id: number;
  action_id: number;
  action_code: string;
  action_label: string;
  action_date: string | null;
  group_key: string;
  group_name: string;
  title: string;
  due: string | null;
  late: boolean;
  state: ChecklistItemState;
  responsible_role: RoleKey | null;
  responsible_user_name: string | null;
}
export interface SeasonCounter {
  key: string;
  label: string;
  code: SeasonCode | null;
  year: number;
  count: number;
  current: boolean;
}
export interface ForesterCandidate {
  id: number;
  display_name: string;
  organisation_name: string | null;
  city: string | null;
  region: string | null;
  state_code: string | null;
  rating_grade: RatingGrade | null;
  rating_reason: string | null;
  last_price: { price: number; year: number } | null;
  contract_year: number | null;
  confirmations: { done: number; total: number };
  action_count: number;
  last_area_state: string | null;
  in_region: boolean;
}

/* ---------- Regeln ---------- */
export function isFilled(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return String(v).trim() !== '';
}

/** 6.3 Zustand eines Punkts */
export function itemState(item: { needed_field: string | null; done_rule: DoneRule }, values: ChecklistValues): ChecklistItemState {
  const v = values ?? {};
  const d = item.done_rule;
  const nf = item.needed_field;
  if (nf && typeof v[nf] === 'string' && /^(Nein|nicht benötigt)$/.test(v[nf] as string)) return 'not_needed';
  let state: ChecklistItemState = 'open';
  if ('never' in d) state = 'open';
  else if ('f' in d && d.is.includes(String(v[d.f] ?? ''))) state = 'done';
  else if ('all' in d && d.all.every((k) => isFilled(v[k]))) state = 'done';
  else if ('any' in d && d.any.some((k) => isFilled(v[k]))) state = 'done';
  else if ('anyOr' in d && d.anyOr.some((k) => isFilled(v[k]))) state = 'done';
  else if ('checks' in d) {
    const c = v[d.checks] as Record<string, unknown> | undefined;
    if (c && d.min.every((m) => c[m])) state = 'done';
  }
  if (state === 'open' && nf && v[nf] === 'erledigt') state = 'done';
  return state;
}

/** Standardfrist aus Aktionstag und Versatz */
export function defaultDue(actionDate: string | null | undefined, offset: number | null | undefined): string | null {
  if (!actionDate || offset == null) return null;
  return addDays(actionDate, offset);
}
/** Fälligkeit: Erinnerung vor Frist */
export function effectiveDue(item: { reminder_date?: string | null; due_date: string | null }): string | null {
  return item.reminder_date || item.due_date || null;
}
export function isLate(item: { state: ChecklistItemState; optional: boolean; reminder_date?: string | null; due_date: string | null }, today: string): boolean {
  const due = effectiveDue(item);
  return item.state === 'open' && !item.optional && !!due && due < today;
}

/** 6.4 Fortschritt je Gewerk; Qualitätssicherung erst nach dem Aktionstag */
export function groupProgress<T extends { id: number; template_item_key: string; group_key: string; title: string; optional: boolean; state: ChecklistItemState; due_date: string | null; reminder_date?: string | null; sort: number }>(
  items: T[],
  groups: ChecklistGroupDef[],
  today: string,
  actionDate: string | null,
): GroupProgress[] {
  const past = !!actionDate && actionDate < today;
  return groups
    .filter((g) => !g.after_action || past)
    .map((g) => {
      const rel = items.filter((i) => i.group_key === g.key && !i.optional && i.state !== 'not_needed');
      const done = rel.filter((i) => i.state === 'done').length;
      const open = rel
        .filter((i) => i.state === 'open')
        .map((i) => ({ id: i.id, key: i.template_item_key, title: i.title, due: effectiveDue(i), late: isLate(i, today) }))
        .sort((a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999'));
      return { key: g.key, name: g.name, responsible_role: g.responsible_role, done, total: rel.length, late: open.filter((o) => o.late).length, open };
    });
}
export function totalProgress(p: GroupProgress[]): { done: number; total: number; pct: number; late: number; open: number } {
  const done = p.reduce((s, g) => s + g.done, 0);
  const total = p.reduce((s, g) => s + g.total, 0);
  return { done, total, pct: total ? Math.round((100 * done) / total) : 0, late: p.reduce((s, g) => s + g.late, 0), open: p.reduce((s, g) => s + g.open.length, 0) };
}

/** 6.2 Status automatisch */
export function deriveStatus(
  a: { date: string | null; forester_contact_id: number | null; area_id: number | null; status_manual: boolean; status: ActionStatus },
  items: { template_item_key: string; state: ChecklistItemState }[],
  today: string,
): ActionStatus {
  if (a.status_manual) return a.status;
  if (a.date && a.date < today) return 'abgeschlossen';
  if (!a.forester_contact_id || !a.area_id) return 'offen';
  const fo = items.find((i) => i.template_item_key === 'foerster')?.state === 'done';
  // Spendenpartner muss den Termin ebenfalls bestätigen (Review 26.10.2026); ältere Aktionen ohne den Punkt zählen als bestätigt
  const pt = (items.find((i) => i.template_item_key === 'partner_termin')?.state ?? 'done') !== 'open';
  const lo = (items.find((i) => i.template_item_key === 'lotsen')?.state ?? 'not_needed') !== 'open';
  if (fo && pt && lo) return 'bestaetigt';
  if (!a.date) return 'abstimmung';
  return 'zugesagt';
}

/** 6.5 Pflanzlotsenbedarf */
export function guidesNeeded(participants: number | null | undefined, rule: { participants_per_guide: number; tww_guides: number } = { participants_per_guide: 10, tww_guides: 1 }): { needed: number; tww: number; external: number } {
  const per = rule.participants_per_guide > 0 ? rule.participants_per_guide : 10;
  const needed = Math.max(1, Math.ceil((participants ?? 0) / per));
  const tww = Math.min(needed, rule.tww_guides ?? 1);
  return { needed, tww, external: needed - tww };
}
export const guidesText = (g: { needed: number; tww: number; external: number }) => `Bedarf: ${g.needed} Pflanzlots${g.needed === 1 ? 'e' : 'en'}, ${g.tww} von TWW, ${g.external} aus Forst oder Ehrenamt`;

/** 6.1 Kennung */
export function actionCode(p: { pattern?: string; prefix: string; date: string | null | undefined; partner: string; ort: string }): string {
  const pattern = p.pattern ?? '{prefix}{yymmdd}_{partner}_{ort}';
  const code = buildCode(pattern, { prefix: p.prefix, date: p.date || '', partner: p.partner || 'Partner', ort: p.ort || 'Ort' });
  return p.date ? code : code.replace(`${p.prefix}_`, `${p.prefix}JJMMTT_`);
}

/** „Fehlt noch“ (Prototyp T.fehlt) */
export function missingText(status: ActionStatus, a: { area_id: number | null; forester_contact_id: number | null }, p: GroupProgress[]): ActionMissing {
  if (status === 'ausgefallen') return { text: 'ausgefallen', late: false };
  if (status === 'offen') {
    const m: string[] = [];
    if (!a.area_id) m.push('Fläche');
    if (!a.forester_contact_id) m.push('Förster');
    return { text: `${m.join(' und ')} offen`, late: true };
  }
  const t = totalProgress(p);
  if (t.open === 0) return { text: 'vollständig', late: false, ok: true };
  const open = `${t.open} ${t.open === 1 ? 'Punkt' : 'Punkte'} offen`;
  return { text: t.late ? `${open}, ${t.late} überfällig` : open, late: t.late > 0 };
}

/** Kurzfassung der Werte (Prototyp taskSummary) */
export function itemSummary(fields: ChecklistFieldDef[], values: ChecklistValues, neededField: string | null, contactNames: Record<string, string[]> = {}): string {
  const parts: string[] = [];
  for (const f of fields) {
    const x = values?.[f.key];
    if (f.type === 'reminder') {
      const r = x as Reminder | undefined;
      if (r && (r.date || r.note)) parts.push(`Erinnerung${r.date ? ' ' + fmtDate(r.date) : ''}${r.note ? ': ' + r.note : ''}`);
      continue;
    }
    if (!isFilled(x)) continue;
    if (f.type === 'dates') parts.push(`${f.label.replace(' am', '')} ${(x as string[]).map(fmtDate).join(', ')}`);
    else if (f.type === 'date') parts.push(`${f.label.split(' (')[0]} ${fmtDate(x as string)}`);
    else if (f.type === 'file') parts.push(`${(x as unknown[]).length} Datei${(x as unknown[]).length > 1 ? 'en' : ''}`);
    else if (f.type === 'checks') parts.push(`${Object.keys(x as object).length} von ${f.options?.length ?? 0}`);
    else if (f.type === 'contact') {
      const names = contactNames[f.key] ?? [];
      parts.push(names.length ? names.join(', ') : Array.isArray(x) ? `${x.length} Kontakt${x.length > 1 ? 'e' : ''}` : String(x));
    } else if (f.type === 'select' && f.key === neededField) parts.push(String(x));
    else if (f.type === 'select') parts.push(`${f.label}: ${x}`);
    else parts.push(String(x).length > 40 ? String(x).slice(0, 40) + '…' : String(x));
  }
  return parts.slice(0, 4).join(' · ');
}

/** Fristanzeige („bis 20.10.“, „überfällig seit 05.09.“, „erledigt“, „nicht benötigt“, „kein Termin“) */
export function dueLabel(item: { state: ChecklistItemState; effective_due: string | null; late: boolean; optional: boolean }): { text: string; cls: 'late' | 'done' | 'nn' | 'mut' | 'due' } {
  if (item.state === 'done') return { text: 'erledigt', cls: 'done' };
  if (item.state === 'not_needed') return { text: 'nicht benötigt', cls: 'nn' };
  if (!item.effective_due) return { text: item.optional ? 'optional' : 'kein Termin', cls: 'mut' };
  const d = fmtDate(item.effective_due).slice(0, 6);
  return item.late ? { text: `überfällig seit ${d}`, cls: 'late' } : { text: `bis ${d}`, cls: 'due' };
}

/** 6.9 Beteiligtenstatus aus Datumsfeldern eines Punkts */
export function contactStatusFromValues(values: ChecklistValues): { status: ActionContactStatus; requested_at: string | null; confirmed_at: string | null } {
  const first = (k: string): string | null => {
    const v = values[k];
    if (Array.isArray(v) && v.length) return String(v[0]);
    if (typeof v === 'string' && v) return v;
    return null;
  };
  const confirmed = first('bestaetigt') ?? first('gebucht') ?? first('bestellt');
  const requested = first('angefragt') ?? first('angebot');
  if (values.hotelstatus === 'gebucht') return { status: 'gebucht', requested_at: requested, confirmed_at: confirmed };
  if (values.hotelstatus === 'Absage') return { status: 'abgesagt', requested_at: requested, confirmed_at: null };
  if (confirmed) return { status: isFilled(values.gebucht) || isFilled(values.bestellt) ? 'gebucht' : 'bestaetigt', requested_at: requested, confirmed_at: confirmed };
  if (requested || values.hotelstatus === 'angefragt') return { status: 'angefragt', requested_at: requested, confirmed_at: null };
  return { status: 'geplant', requested_at: null, confirmed_at: null };
}

/**
 * Darf die Rolle den Punkt bearbeiten? Voraussetzung checklists write; dann passt die Zuständigkeit (Punkt, sonst Gewerk)
 * zur Rolle. Administration und forstfachliche Leitung (Aktionsleitung) dürfen alle Punkte; Flächenmanagement teilt sich
 * das Gewerk Forst. Abnahme Plan 04: Projektmanagement bearbeitet Event-Punkte, nicht Forst-Punkte (Hinweis statt Fehler).
 */
export function canEditItem(perms: { actions?: string; checklists?: string }, roles: string[], groupRole: RoleKey | null, itemRole: RoleKey | null): boolean {
  if (perms.checklists !== 'write') return false;
  if (roles.includes('admin') || roles.includes('forst')) return true;
  const r = itemRole ?? groupRole;
  if (!r) return true;
  if (roles.includes(r)) return true;
  if (r === 'forst' && roles.includes('flaeche')) return true;
  return false;
}

/** Hinweise in der Akte (5.5 Übersicht) */
export function actionHints(a: { area_id: number | null; forester_contact_id: number | null; date: string | null; status: ActionStatus }, items: { template_item_key: string; state: ChecklistItemState; values: ChecklistValues }[], guides: { needed: number; confirmed: number; forester: number }): ActionDetail['hints'] {
  const out: ActionDetail['hints'] = [];
  if (a.status === 'ausgefallen' || a.status === 'abgeschlossen') return out;
  const by = (k: string) => items.find((i) => i.template_item_key === k);
  if (!a.area_id) out.push({ kind: 'bad', text: 'Noch keine Fläche zugeordnet' });
  if (!a.forester_contact_id) out.push({ kind: 'bad', text: 'Noch kein Förster zugeordnet', item_key: 'foerster' });
  else if (by('foerster')?.state !== 'done') out.push({ kind: 'warn', text: 'Termin vom Förster noch nicht bestätigt', item_key: 'foerster' });
  if (by('partner_termin')?.state === 'open') out.push({ kind: 'warn', text: 'Termin vom Spendenpartner noch nicht bestätigt', item_key: 'partner_termin' });
  if (!a.date) out.push({ kind: 'warn', text: 'Aktionstag steht noch nicht fest' });
  const lotsen = by('lotsen');
  if (lotsen && lotsen.state === 'open' && guides.confirmed + guides.forester < guides.needed) out.push({ kind: 'warn', text: `Pflanzlotsen fehlen: ${guides.confirmed + guides.forester} von ${guides.needed}`, item_key: 'lotsen' });
  const v = by('vertrag');
  if (v && v.state === 'open') out.push({ kind: 'warn', text: v.values.versendet ? 'Projektvereinbarung versendet, Rücklauf fehlt' : 'Projektvereinbarung noch nicht versendet', item_key: 'vertrag' });
  return out;
}

/** Ordner einer Aktion für Dateien eines Checklistenpunkts: Titel ohne Klammerzusatz, „/“ würde einen Unterordner bilden */
export const checklistFolder = (title: string): string => title.split(' (')[0]!.replace(/\//g, '-').trim();
